import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { API_URL } from "../config";
import { useUser } from "../context/UserContext";
import { FaGem, FaStar } from "react-icons/fa";
import { useTitle } from "../hooks/useTitle";
import haptic from "../utils/haptic";
import confetti from "canvas-confetti";
import { toast } from "react-toastify";
import "./Game.css";
import chestAnimation from "../animations/chest.json";

const CHEST_COST = 50;

function MysteryChestGame() {
  useTitle("Mystery Chest");
  const navigate = useNavigate();
  const { userData, setUserData, APIKey } = useUser();
  const [phase, setPhase] = useState("idle"); // idle | shaking | opening | result
  const [result, setResult] = useState(null);
  const lottieRef = useRef(null);
  const animCompleteRef = useRef(null); // resolves when Lottie animation finishes

  const handleOpen = async () => {
    if ((userData.user?.points || 0) < CHEST_COST) {
      toast.error("Not enough Gems! You need 500 Gems to open a chest.");
      haptic.error();
      return;
    }
    haptic.tap();
    setPhase("shaking");
    setResult(null);

    // Pause (hold first frame) while shaking
    lottieRef.current?.goToAndStop(0, true);

    try {
      // Fire API and shake for at least 1.5s
      const [res] = await Promise.all([
        axios.post(`${API_URL}/api/game/chest/open`, {
          userId: userData.userId,
          initData: WebApp.initData,
        }, { headers: { "x-api-key": APIKey } }),
        new Promise(r => setTimeout(r, 1500)),
      ]);

      // Play the Lottie opening animation and wait for it to finish naturally
      setPhase("opening");
      lottieRef.current?.goToAndPlay(0, true);

      // Resolved by the Lottie onComplete callback — no hardcoded timeout
      await new Promise(r => { animCompleteRef.current = r; });

      setResult(res.data);
      setPhase("result");

      setUserData(prev => ({
        ...prev,
        user: { ...prev.user, points: res.data.user.points },
      }));

      if (res.data.isJackpot) {
        haptic.success();
        confetti({ particleCount: 250, spread: 120, origin: { y: 0.5 }, colors: ["#ffd700", "#ff69b4", "#00ffff", "#ffffff"] });
      } else if (res.data.wonAmount >= 600) {
        haptic.success();
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      } else {
        haptic.tap();
      }
    } catch (err) {
      setPhase("idle");
      lottieRef.current?.goToAndStop(0, true);
      toast.error(err.response?.data?.error || "Error opening chest.");
    }
  };

  const handleReset = () => {
    setPhase("idle");
    setResult(null);
    lottieRef.current?.goToAndStop(0, true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-[#0a0f1a] text-white min-h-screen pb-28 pt-20"
    >
      <div className="container mx-auto px-4 mt-2">
        <div className="flex flex-col items-center">

          {/* Jackpot Display replacing Balance */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur px-6 py-2 rounded-full border border-amber-500/30 mb-4 flex items-center gap-3 shadow-[0_0_15px_rgba(245,158,11,0.15)] animate-pulse">
            <span className="text-amber-500 font-black tracking-widest uppercase text-sm">🎰 Jackpot:</span>
            <span className="font-black text-amber-400 flex items-center gap-1 text-2xl drop-shadow-md">
              500 <FaGem size={18} />
            </span>
          </div>

          {/* Lottie Chest — shrinks when result is ready so it stays above the fold */}
          <div className="relative flex items-center justify-center mb-3">
            <motion.div
              animate={{ width: phase === "result" ? 160 : 208, height: phase === "result" ? 160 : 208 }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
              className={`drop-shadow-[0_0_40px_rgba(245,158,11,0.5)] ${phase === "shaking" ? "chest-shake" : ""}`}
              onClick={phase === "idle" ? handleOpen : undefined}
              style={{ cursor: phase === "idle" ? "pointer" : "default" }}
            >
              <Lottie
                lottieRef={lottieRef}
                animationData={chestAnimation}
                loop={false}
                autoplay={false}
                onComplete={() => animCompleteRef.current?.()}
              />
            </motion.div>

            {/* Light burst when opening */}
            <AnimatePresence>
              {phase === "opening" && (
                <motion.div
                  key="burst"
                  className="absolute inset-0 rounded-full bg-amber-400/30 blur-2xl pointer-events-none"
                  initial={{ scale: 0.4, opacity: 1 }}
                  animate={{ scale: 2.5, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              )}
            </AnimatePresence>

            {/* Idle pulse ring */}
            {phase === "idle" && (
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-amber-400/30 pointer-events-none"
                animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.15, 0.5] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </div>

          {/* Button / Status / Result area */}
          <AnimatePresence mode="wait">
            {phase === "idle" && (
              <motion.div key="idle" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="w-full max-w-xs flex flex-col items-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={handleOpen}
                  disabled={(userData.user?.points || 0) < CHEST_COST}
                  className="w-full py-5 font-black text-xl rounded-3xl bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-xl shadow-orange-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Open Chest — 50 💎
                </motion.button>
              </motion.div>
            )}

            {(phase === "shaking" || phase === "opening") && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
                <div className="flex gap-1.5">
                  {[0, 1, 2].map(i => (
                    <motion.div key={i} className="w-3 h-3 rounded-full bg-amber-400" animate={{ y: [-6, 0, -6] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />
                  ))}
                </div>
                <p className="text-slate-400 font-semibold text-sm">{phase === "opening" ? "Revealing…" : "Unlocking…"}</p>
              </motion.div>
            )}

            {phase === "result" && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="w-full max-w-xs flex flex-col items-center gap-4"
              >
                {result.isJackpot ? (
                  <div className="w-full text-center bg-gradient-to-br from-yellow-400/20 to-orange-500/20 border-2 border-amber-400 rounded-3xl p-6">
                    <div className="text-5xl mb-2">🎉</div>
                    <p className="text-amber-300 font-black text-2xl mb-1">JACKPOT!</p>
                    <p className="text-white font-bold text-lg">You won <span className="text-amber-400 text-3xl font-black">500 💎</span></p>
                    <p className="text-slate-400 text-xs mt-2">You were the lucky one when the pool filled up!</p>
                    <div className="flex justify-center gap-1 mt-3">
                      {[...Array(5)].map((_, i) => <FaStar key={i} className="text-amber-400" size={18} />)}
                    </div>
                  </div>
                ) : (() => {
                  const isProfit = result.wonAmount >= CHEST_COST;
                  const diff = Math.abs(result.wonAmount - CHEST_COST);
                  return (
                    <div className={`w-full text-center rounded-3xl p-5 border-2 ${
                      isProfit
                        ? "bg-emerald-500/15 border-emerald-400"
                        : "bg-red-500/15 border-red-500"
                    }`}>
                      {/* Badge */}
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3 ${
                        isProfit ? "bg-emerald-500/30 text-emerald-300" : "bg-red-500/30 text-red-300"
                      }`}>
                        {isProfit ? "✦ WIN" : "✕ LOSS"}
                      </div>
                      <p className="text-slate-300 font-semibold text-sm mb-1">You got</p>
                      <p className={`font-black text-5xl mb-2 ${
                        isProfit ? "text-emerald-400" : "text-red-400"
                      }`}>
                        {result.wonAmount} 💎
                      </p>
                      <p className={`text-sm font-bold ${
                        isProfit ? "text-emerald-400" : "text-red-400"
                      }`}>
                        {isProfit ? `+${diff} profit` : `-${diff} lost`}
                      </p>
                    </div>
                  );
                })()}

                <div className="flex gap-3 w-full">
                  <button onClick={handleReset} className="flex-1 py-4 font-bold rounded-2xl bg-slate-700 hover:bg-slate-600 transition-colors">
                    Open Again
                  </button>
                  <button onClick={() => navigate("/game")} className="flex-1 py-4 font-bold rounded-2xl bg-slate-800 hover:bg-slate-700 transition-colors text-slate-300">
                    Back
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

export default MysteryChestGame;
